import { useMemo, useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, Cell,
} from "recharts";
import { ArrowRight } from "lucide-react";
import { ciBaseline, ciAfter, ciPareto, ciStratify, ciMigrationByReagent, ciInstrumentBox, ciEffectiveness, CI_SPEC, CI_PRODUCT } from "../data/synthetic";
import { ChartPanel, FlowDiagram, Disclaimer, CountUp, tooltipStyle, axisProps, TechTag } from "./common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const GRID = <CartesianGrid stroke="rgba(32,184,205,0.08)" vertical={false} />;

const EVIDENCE_PATH = [
  { title: "Evidence Pathway", items: [
    { label: "Observed Yield Loss", detail: "FPY erosion and elevated retest rate quantified against baseline." },
    { label: "High Migration Time Dominates Pareto", detail: "One failure mode carries the largest share of the loss — effort concentrates there." },
    { label: "Instrument Analysis Inconclusive", detail: "No instrument fully explains the population shift; equipment blame is not supported." },
    { label: "Reagent Comparison Reveals Movement", detail: "A specific reagent lot shows a systematic upward migration-time shift." },
    { label: "Stable Directional Trend", detail: "Historical trend shows the shift is sustained, not episodic." },
    { label: "Capability Review", detail: "Process-capability review indicates acceptance criteria may no longer align optimally with demonstrated process behaviour." },
    { label: "Change-History Review", detail: "Process/specification alignment opportunity identified in change history." },
    { label: "Controlled Change Evaluated", detail: "A specification-alignment change is progressed through Quality review and change control." },
  ]},
];

const IMPROVEMENT_ACTIONS = [
  ["Specification review", "Reassess acceptance criteria against demonstrated process capability.", "Change Control"],
  ["Process-alignment assessment", "Confirm upstream process setpoints and downstream criteria tell one story.", "CAPA"],
  ["Work-instruction review", "Update SOP/WI where practice must match the revised expectation.", "SOP / WI"],
  ["Manufacturing communication", "Brief affected teams on rationale, scope and expected behaviour.", "Quality Review"],
  ["Controlled implementation", "Execute through ECO/change control with traceable approvals.", "ECO"],
  ["Operator / technical training", "Train where the change alters execution or interpretation.", "Training"],
  ["Post-change effectiveness monitoring", "Track FPY, recurrence, scrap and retest rate over a defined period.", "Effectiveness Check"],
];

function BoxRow({ label, box, min, max }) {
  const pct = (v) => `${((v - min) / (max - min)) * 100}%`;
  return (
    <div className="flex items-center gap-3" data-testid={`box-${label}`}>
      <span className="font-mono text-[10px] text-pale/70 w-16 shrink-0">{label}</span>
      <div className="relative flex-1 min-w-0 h-6">
        <div className="absolute top-1/2 w-full h-px bg-teal/30" />
        <div className="absolute top-1/2 h-px bg-pale/50" style={{ left: pct(box.min), width: `calc(${pct(box.max)} - ${pct(box.min)})` }} />
        <div className="absolute top-1 bottom-1 border border-cyan/70 bg-cyan/15" style={{ left: pct(box.q1), width: `calc(${pct(box.q3)} - ${pct(box.q1)})` }} />
        <div className="absolute top-0 bottom-0 w-0.5 bg-cyan" style={{ left: pct(box.median) }} />
      </div>
      <span className="font-mono text-[10px] text-steel w-14 text-right">n={box.n}</span>
    </div>
  );
}

export default function CiCaseStudy() {
  const base = useMemo(ciBaseline, []);
  const after = useMemo(ciAfter, []);
  const pareto = useMemo(ciPareto, []);
  const [dim, setDim] = useState("instrument");
  const strat = useMemo(() => ciStratify(dim), [dim]);
  const reagentStats = useMemo(ciMigrationByReagent, []);
  const boxes = useMemo(ciInstrumentBox, []);
  const effectiveness = useMemo(ciEffectiveness, []);
  const [phase, setPhase] = useState("before");
  const show = phase === "before" ? base : { ...after, runs: base.runs };

  return (
    <div className="mt-16" data-testid="ci-case-study">
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="font-sans tracking-tight text-2xl text-paper">Flagship Case Study — Yield Recovery &amp; Scrap Reduction Program</h3>
        <span className="tech-tag">Platform: {CI_PRODUCT}</span>
      </div>
      <p className="text-sm text-steel mt-3 max-w-3xl leading-relaxed">
        Recurring analytical and process-related failures reduced first-pass yield, increased repeat testing and contributed to avoidable manufacturing
        scrap. This case study runs live on the same synthetic dataset as the Analytics Lab.
      </p>
      <div className="mt-4 max-w-xl">
        <Disclaimer>Synthetic demonstration metrics — illustrative values generated from the portfolio's deterministic dataset, not employer data.</Disclaimer>
      </div>

      {/* Current state */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8" data-testid="ci-current-state">
        {[
          ["First Pass Yield", base.fpy, "%", 1],
          ["Final Yield", base.finalYield, "%", 1],
          ["Retest Rate", base.retestRate, "%", 1],
          ["Synthetic Scrap Index", base.scrapIndex, "", 0],
        ].map(([label, v, suffix, dec]) => (
          <div key={label} className="panel p-4" data-testid={`ci-baseline-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-steel">{label}</p>
            <p className="text-2xl text-cyan font-semibold mt-2">
              <CountUp value={v} decimals={dec} suffix={suffix} />
            </p>
          </div>
        ))}
      </div>

      {/* Pareto + stratification */}
      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <ChartPanel title="Failure Pareto — RapidSDS-X" subtitle="the vital few drive the loss" testId="ci-pareto">
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={pareto} margin={{ left: -14, right: 8, top: 4 }}>
              {GRID}
              <XAxis dataKey="mode" {...axisProps} interval={0} angle={-30} textAnchor="end" height={80} />
              <YAxis yAxisId="l" {...axisProps} />
              <YAxis yAxisId="r" orientation="right" domain={[0, 100]} {...axisProps} />
              <Tooltip {...tooltipStyle} />
              <Bar yAxisId="l" dataKey="count" name="Failures">
                {pareto.map((e, i) => (
                  <Cell key={e.mode} fill={i === 0 ? "#FFB347" : "#20B8CD"} />
                ))}
              </Bar>
              <Line yAxisId="r" type="monotone" dataKey="cumulative" stroke="#E7F6F8" strokeWidth={1.5} dot={false} name="Cumulative %" />
            </ComposedChart>
          </ResponsiveContainer>
          <p className="text-xs text-steel mt-2">
            <span className="text-warn font-mono">High Migration Time</span> is the top loss driver — the program concentrates there first.
          </p>
        </ChartPanel>

        <ChartPanel
          title="Stratification Explorer"
          subtitle="aggregate failure looks broad — stratification concentrates it"
          testId="ci-stratification"
          action={
            <Select value={dim} onValueChange={setDim}>
              <SelectTrigger className="bg-navy border-teal/40 text-pale text-xs h-8 w-44" data-testid="ci-strat-dimension">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy2 border-cyan/25 text-pale">
                {[["instrument", "Instrument"], ["reagent_lot", "Reagent Lot"], ["component_lot", "Component Lot"], ["month", "Month"], ["qc_stage", "QC Stage"], ["attempt_type", "Initial vs Retest"]].map(([k, l]) => (
                  <SelectItem key={k} value={k} className="text-xs font-mono focus:bg-teal/30 focus:text-paper">{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        >
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={strat.slice(0, 14)} margin={{ left: -14, right: 8, top: 4 }}>
              {GRID}
              <XAxis dataKey="name" {...axisProps} interval={0} angle={-35} textAnchor="end" height={70} />
              <YAxis yAxisId="l" {...axisProps} />
              <YAxis yAxisId="r" orientation="right" domain={[0, 100]} {...axisProps} />
              <Tooltip {...tooltipStyle} />
              <Bar yAxisId="l" dataKey="failureRate" name="Failure Rate %" fill="#20B8CD" />
              <Line yAxisId="r" type="monotone" dataKey="hmtShare" stroke="#FFB347" strokeWidth={1.5} dot={false} name="HMT Share of Failures %" />
            </ComposedChart>
          </ResponsiveContainer>
          <p className="text-xs text-steel mt-2">
            Switch dimensions: where the amber line concentrates, the failure has an address.
          </p>
        </ChartPanel>
      </div>

      {/* Statistical analysis */}
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <ChartPanel title="Instrument Comparison — Migration-Time Box Plots" subtitle={`min / Q1 / median / Q3 / max • ${CI_SPEC.label}`} testId="ci-boxplots">
          <div className="space-y-2.5 py-2">
            {boxes.map((b) => (
              <BoxRow key={b.instrument} label={b.instrument} box={b} min={6} max={18} />
            ))}
          </div>
          <p className="text-xs text-steel mt-2">Spreads overlap heavily between instruments — equipment alone does not explain the population shift.</p>
        </ChartPanel>

        <ChartPanel title="Reagent-Lot Capability — Synthetic Spec Window" subtitle={`LSL ${CI_SPEC.lsl} / USL ${CI_SPEC.usl} min (synthetic)`} testId="ci-capability">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[420px]">
              <thead>
                <tr className="border-b border-cyan/15">
                  {["Lot", "Mean", "σ", "Cp", "Cpk", ""].map((h) => (
                    <th key={h} className="font-mono text-[10px] uppercase tracking-wider text-steel py-1.5 pr-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reagentStats.map((r) => (
                  <tr key={r.lot} className={`border-b border-cyan/8 ${r.outlier ? "bg-warn/5" : ""}`} data-testid={`ci-cap-${r.lot}`}>
                    <td className="font-mono text-xs text-paper py-1.5 pr-3">{r.lot}</td>
                    <td className="font-mono text-xs text-pale/80 py-1.5 pr-3">{r.mean}</td>
                    <td className="font-mono text-xs text-pale/80 py-1.5 pr-3">{r.sd}</td>
                    <td className="font-mono text-xs text-pale/80 py-1.5 pr-3">{r.cp}</td>
                    <td className={`font-mono text-xs py-1.5 pr-3 ${r.outlier ? "text-warn" : "text-pale/80"}`}>{r.cpk}</td>
                    <td className="font-mono text-[10px] text-warn py-1.5">{r.outlier ? "shifted" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-steel mt-2">
            RGT-107's centred-but-shifted population against the window is the kind of signal that turns a recurring failure into a process/specification
            alignment question.
          </p>
        </ChartPanel>
      </div>

      {/* Root cause story */}
      <div className="grid lg:grid-cols-2 gap-6 mt-8 items-start">
        <div>
          <h4 className="font-sans tracking-tight text-lg text-paper mb-4">Root-Cause Evidence Pathway</h4>
          <FlowDiagram layers={EVIDENCE_PATH} testId="ci-evidence-path" defaultNote="Follow the evidence: each step either strengthens or eliminates an explanation." />
        </div>
        <div>
          <h4 className="font-sans tracking-tight text-lg text-paper mb-4">Hypothetical Controlled Improvement Package</h4>
          <div className="space-y-2" data-testid="ci-improvement-actions">
            {IMPROVEMENT_ACTIONS.map(([action, detail, qms]) => (
              <div key={action} className="panel p-3.5 flex items-start gap-3">
                <ArrowRight className="w-3.5 h-3.5 text-cyan mt-1 shrink-0" aria-hidden="true" />
                <div className="flex-1">
                  <p className="font-sans text-sm font-semibold text-paper">{action}</p>
                  <p className="text-xs text-steel mt-1">{detail}</p>
                </div>
                <TechTag>{qms}</TechTag>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Before / After */}
      <div className="panel p-6 mt-8" data-testid="ci-before-after">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="overline-label">Before / After — Modelled Outcome</p>
            <p className="text-xs text-steel mt-1">Synthetic demonstration metrics illustrating the improvement methodology.</p>
          </div>
          <div className="flex border border-teal/40 rounded-sm overflow-hidden" data-testid="ci-before-after-toggle">
            {["before", "after"].map((p) => (
              <button
                key={p}
                data-testid={`ci-toggle-${p}`}
                onClick={() => setPhase(p)}
                className={`px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors ${
                  phase === p ? (p === "before" ? "bg-warn text-navy" : "bg-success text-navy") : "text-pale/70 hover:text-paper"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          {[
            ["FPY", show.fpy, "%", 1, "higher is better"],
            ["Retest Rate", show.retestRate, "%", 1, "lower is better"],
            ["Synthetic Scrap Index", show.scrapIndex ?? after.scrapIndex, "", 0, "baseline = 100"],
            ["HMT Recurrence", phase === "before" ? "Elevated" : "Substantially reduced", "", 0, ""],
          ].map(([label, v, suffix, dec, hint]) => (
            <div key={label} className="panel p-4 bg-navy">
              <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-steel">{label}</p>
              <p className={`text-2xl font-semibold mt-2 font-mono ${phase === "after" ? "text-success" : "text-warn"}`} data-testid={`ci-ba-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {typeof v === "number" ? <CountUp key={phase} value={v} decimals={dec} suffix={suffix} duration={0.6} /> : v}
              </p>
              {hint && <p className="font-mono text-[9px] text-steel/70 mt-1">{hint}</p>}
            </div>
          ))}
        </div>
        <div className="mt-5 grid md:grid-cols-2 gap-3">
          <Disclaimer>The synthetic before/after comparison demonstrates methodology — it is not the source dataset for any professional-impact claim.</Disclaimer>
          <div className="panel p-3 border-l-2 border-l-success" data-testid="ci-professional-impact">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-steel">Actual professional impact (separate)</p>
            <p className="font-mono text-xl text-success mt-1">~45% Scrap Reduction</p>
            <p className="text-xs text-steel mt-1">CAPA/RCA-driven process improvement from professional experience.</p>
          </div>
        </div>
      </div>

      {/* Effectiveness monitoring */}
      <div className="mt-6">
        <ChartPanel
          title="Effectiveness Monitoring — Post-Change Trajectory"
          subtitle="baseline → implementation → 30 / 60 / 90 days (synthetic)"
          testId="ci-effectiveness-chart"
        >
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={effectiveness} margin={{ left: -14, right: 0, top: 4 }}>
              {GRID}
              <XAxis dataKey="phase" {...axisProps} />
              <YAxis yAxisId="l" domain={[0, 100]} {...axisProps} />
              <YAxis yAxisId="r" orientation="right" domain={[0, 110]} {...axisProps} />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
              <Bar yAxisId="r" dataKey="scrapIndex" fill="rgba(255,179,71,0.35)" name="Scrap Index" />
              <Line yAxisId="l" type="monotone" dataKey="fpy" stroke="#34C759" strokeWidth={2} name="FPY %" />
              <Line yAxisId="l" type="monotone" dataKey="retestRate" stroke="#20B8CD" strokeWidth={2} name="Retest Rate %" />
              <Line yAxisId="l" type="monotone" dataKey="recurrence" stroke="#FF3B30" strokeWidth={2} strokeDasharray="5 3" name="HMT Recurrence %" />
            </ComposedChart>
          </ResponsiveContainer>
          <p className="text-xs text-steel mt-3 leading-relaxed">
            An improvement is not considered sustained simply because the first few runs pass. Effectiveness monitoring evaluates whether performance
            remains stable over an appropriate period.
          </p>
        </ChartPanel>
      </div>
    </div>
  );
}
